import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useTrackClickedCollectContent } from '@air/analytics';
import { MobileNavigation, type MobileNavigationItem } from '@air/component-mobile-navigation';
import { BellIcon, DashedPlusIcon, MenuIcon, PhotosIcon, SquarePlusIcon } from '@air/design-icons';
import { type ActionSheetOption } from '@air/primitive-action-sheet';
import { useAirModal } from '@air/provider-modal';

import { MobileNotifications } from '~/components/AirNotifications/MobileNotifications';
import { ActionSheetModal } from '~/components/Mobile/ActionSheetModal';
import { AirMobileWorkspaceNavigation } from '~/components/Mobile/CoreMobileWorkspaceNavigation';
import { ContentCollectionModal } from '~/components/Modals/ContentCollectionModal/ContentCollectionModal';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { useUploadContext } from '~/components/PrivateDropzone/UploadContext';
import { BOARDS_SELECT_EMPTY_OPTION } from '~/constants/BoardSearch';
import { useCurrentBoardPermissions } from '~/hooks/useCurrentBoardPermissions';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { useCanUploadAssets } from '~/swr-hooks/members/useCanUploadAssets';
import { useActiveSubscriptionCallback } from '~/swr-hooks/subscriptions/useActiveSubscriptionCallback';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

export const CoreMobileNavigation = memo(() => {
  const { openUploader } = useUploadContext();
  const [showMobileActionsActionSheet, closeMobileActionsActionSheet] = useAirModal(ActionSheetModal);
  const [showCreateBoardModal] = useAirModal(CreateBoardModal);
  const [showContentCollectionModal] = useAirModal(ContentCollectionModal);
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { boardPermissions } = useCurrentBoardPermissions();
  const { activeSubscriptionCallback } = useActiveSubscriptionCallback();
  const centralizedBoard = useSelector(centralizedBoardSelector);
  const { trackClickedCollectContent } = useTrackClickedCollectContent();

  const canCreateRootBoards = canCreateBoard(workspacePermissions);
  const canCreateSubBoard = canCreateBoard(boardPermissions);
  const { canUploadAssets } = useCanUploadAssets();

  const onCollectContentClick = useCallback(() => {
    if (!centralizedBoard) {
      return;
    }
    trackClickedCollectContent();
    showContentCollectionModal({ boardId: centralizedBoard?.id, boardTitle: centralizedBoard.title });
    closeMobileActionsActionSheet();
  }, [centralizedBoard, closeMobileActionsActionSheet, showContentCollectionModal, trackClickedCollectContent]);

  const options = useMemo(() => {
    const optionsArray: ActionSheetOption[] = [];

    if (canCreateRootBoards || canCreateSubBoard) {
      optionsArray.push({
        id: 'create-board',
        label: 'Create a new board',
        onSelect: () => {
          showCreateBoardModal({
            selectedParentBoard: BOARDS_SELECT_EMPTY_OPTION,
            trackLocation: 'mobile-bottom-nav',
          });

          closeMobileActionsActionSheet();
        },
        prefix: <DashedPlusIcon size="sm" />,
        type: 'item',
      });
    }

    if (canUploadAssets) {
      optionsArray.push({
        id: 'upload-from-device',
        label: 'Upload from device',
        onSelect: () => {
          closeMobileActionsActionSheet();

          activeSubscriptionCallback(() => {
            openUploader();
          });
        },
        prefix: <PhotosIcon size="sm" />,
        type: 'item',
      });
    }

    if (centralizedBoard) {
      optionsArray.push(
        {
          id: 'content-collection-separator',
          type: 'separator',
        },
        {
          ...getDropdownMenuOption('content-collection'),
          description: 'Collect content directly from anyone, no Air account needed.',
          onSelect: onCollectContentClick,
          type: 'item',
        },
      );
    }

    return optionsArray;
  }, [
    activeSubscriptionCallback,
    canCreateRootBoards,
    canCreateSubBoard,
    canUploadAssets,
    centralizedBoard,
    closeMobileActionsActionSheet,
    onCollectContentClick,
    openUploader,
    showCreateBoardModal,
  ]);

  const items = useMemo(() => {
    const itemsArray: MobileNavigationItem[] = [
      {
        icon: <MenuIcon size="lg" />,
        id: 'menu',
        label: 'Menu',
        renderContent: ({ onClose }) => <AirMobileWorkspaceNavigation onClose={onClose} />,
      },
    ];

    if (options.length) {
      itemsArray.push({
        icon: <SquarePlusIcon size="lg" />,
        id: 'actions',
        label: 'Actions',
        onClick: () =>
          showMobileActionsActionSheet({
            options,
            title: 'Actions',
          }),
      });
    }

    itemsArray.push({
      icon: <BellIcon size="lg" />,
      id: 'notifications',
      label: 'Notifications',
      renderContent: ({ onClose }) => <MobileNotifications onClose={onClose} />,
    });

    return itemsArray;
  }, [options, showMobileActionsActionSheet]);

  return <MobileNavigation items={items} />;
});

CoreMobileNavigation.displayName = 'CoreMobileNavigation';
